import React, { useEffect, useState } from "react";
import { Minus, Plus, Trash2 } from "lucide-react";


const FIELD_OPTIONS = {
  dosage: ["½ tablet", "1 tablet", "2 tablets", "1 capsule", "2 capsules", "5 mL", "10 mL", "1 puff", "2 puffs", "Apply thin layer", "As directed"],
  frequency: ["Once daily", "Twice daily", "Three times daily", "Four times daily", "Every 6 hours", "Every 8 hours", "At bedtime", "As needed"],
  duration: ["3 days", "5 days", "7 days", "10 days", "14 days", "1 month", "Until review", "As needed"],
  timing: ["After food", "Before food", "With food", "Any time", "At bedtime"],
};

const FIELD_LABELS = {
  dosage: "Dose each time",
  frequency: "How often",
  duration: "For how long",
  timing: "When to take",
};

function GuidedChoice({ field, value, onChange }) {
  const choices = FIELD_OPTIONS[field];
  const [custom, setCustom] = useState(Boolean(value && !choices.includes(value)));

  useEffect(() => {
    if (value && !choices.includes(value)) setCustom(true);
    if (choices.includes(value)) setCustom(false);
  }, [choices, value]);

  const selectValue = custom ? "__custom" : (value || "");
  return <label className="text-xs font-semibold text-slate-600">
    {FIELD_LABELS[field]}
    <select
      aria-label={FIELD_LABELS[field]}
      className="mg-input mt-1 w-full bg-white py-2 text-sm"
      value={selectValue}
      onChange={(event) => {
        if (event.target.value === "__custom") {
          setCustom(true);
          onChange("");
        } else {
          setCustom(false);
          onChange(event.target.value);
        }
      }}
    >
      <option value="">Choose</option>
      {choices.map((choice) => <option key={choice} value={choice}>{choice}</option>)}
      <option value="__custom">Other…</option>
    </select>
    {custom && <input
      autoFocus
      aria-label={`Custom ${FIELD_LABELS[field].toLowerCase()}`}
      className="mg-input mt-2 w-full py-2 text-sm"
      placeholder={`Enter ${FIELD_LABELS[field].toLowerCase()}`}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
    />}
  </label>;
}

function DispenseQuantity({ item, onChange }) {
  const unit = item.pharmacy_unit_name || "base units";
  const numeric = Number(item.pharmacy_quantity || 0);
  const available = Number(item.pharmacy_available_quantity);
  const availableLabel = Number.isFinite(available) ? available.toLocaleString() : item.pharmacy_available_quantity;
  const adjust = (amount) => {
    const next = Math.max(1, (Number.isFinite(numeric) ? numeric : 1) + amount);
    onChange(String(next));
  };
  return <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 sm:col-span-2">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <label htmlFor={`dispense-${item.draft_id}`} className="text-sm font-semibold text-mg-navy">Total quantity to dispense</label>
        <p className="mt-0.5 text-xs text-slate-600">Enter the total number of {unit} the pharmacy should provide.</p>
        {item.pharmacy_lot_number && <p className="mt-1 text-xs font-semibold text-blue-700">Lot {item.pharmacy_lot_number}{item.pharmacy_expiry_date ? ` · Expires ${item.pharmacy_expiry_date}` : ""}</p>}
      </div>
      {item.pharmacy_available_quantity != null && <span className="rounded-full bg-white px-2 py-1 text-xs text-slate-600">Available: {availableLabel} {unit}</span>}
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <button type="button" aria-label="Decrease dispense quantity" className="rounded-lg border border-blue-200 bg-white p-2 text-mg-navy" onClick={() => adjust(-1)}><Minus className="h-4 w-4" /></button>
      <div className="relative w-36">
        <input
          id={`dispense-${item.draft_id}`}
          aria-label={`Dispense quantity in ${unit}`}
          type="number"
          min="0.000001"
          step="1"
          className="mg-input w-full py-2 pr-14 text-center text-base font-semibold"
          value={item.pharmacy_quantity || ""}
          onChange={(event) => onChange(event.target.value)}
        />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-slate-500">{unit}</span>
      </div>
      <button type="button" aria-label="Increase dispense quantity" className="rounded-lg border border-blue-200 bg-white p-2 text-mg-navy" onClick={() => adjust(1)}><Plus className="h-4 w-4" /></button>
      <div className="flex flex-wrap gap-1 pl-1" aria-label="Common dispense quantities">
        {[5, 10, 15, 30].map((quantity) => <button key={quantity} type="button" className="rounded-full border border-blue-200 bg-white px-2.5 py-1 text-xs font-semibold text-mg-medical" onClick={() => onChange(String(quantity))}>{quantity}</button>)}
      </div>
    </div>
  </div>;
}

export function PrescriptionLineEditor({ item, index, medicineEditor, onChange, onRemove }) {
  const pharmacyItem = Boolean(item.pharmacy_product_selection_id);
  return <fieldset className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid={`prescription-line-${index + 1}`}>
    <legend className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Medicine {index + 1}</legend>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <label className="text-xs font-semibold text-slate-600">Medicine</label>
        {pharmacyItem
          ? <div className="mt-1 rounded-lg border border-blue-200 bg-white px-3 py-2"><span className="font-semibold text-mg-navy">{item.medicine_name}</span><span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">Pharmacy item</span></div>
          : <div className="mt-1">{medicineEditor}</div>}
      </div>
      <button type="button" aria-label={`Remove medicine ${index + 1}`} onClick={onRemove} className="mt-5 rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
    </div>

    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <label className="text-xs font-semibold text-slate-600">Strength
        <input aria-label={`Medicine ${index + 1} strength`} className="mg-input mt-1 w-full py-2 text-sm" placeholder="Example: 500 mg" value={item.strength || ""} onChange={(event) => onChange("strength", event.target.value)} />
      </label>
      {Object.keys(FIELD_OPTIONS).map((field) => <GuidedChoice key={field} field={field} value={item[field] || ""} onChange={(value) => onChange(field, value)} />)}
      <label className="text-xs font-semibold text-slate-600 sm:col-span-2 lg:col-span-3">Extra instructions
        <input aria-label={`Medicine ${index + 1} instructions`} className="mg-input mt-1 w-full py-2 text-sm" placeholder="Optional notes for the patient or pharmacist" value={item.instructions || ""} onChange={(event) => onChange("instructions", event.target.value)} />
      </label>
      {pharmacyItem && <DispenseQuantity item={item} onChange={(value) => onChange("pharmacy_quantity", value)} />}
    </div>
  </fieldset>;
}
